import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "../component/Pagination";
import { toast } from "react-toastify";

import {
  fetchBanners,
  createBanner,
  deleteBanner,
  toggleBannerStatus,
  updateBanner,
} from "../../../slice/banner/bannerSlice";

export default function ListBanner() {
  const [page, setPage] = useState(1);
  const limit = 10;

  const dispatch = useDispatch();

  const { banners = [], loading, pagination = {} } = useSelector(
    (state) => state.banner || {}
  );

  // Add Modal
  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState("");

  // Edit Modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editButtonText, setEditButtonText] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [currentBannerImage, setCurrentBannerImage] = useState(null);

  useEffect(() => {
    dispatch(fetchBanners({ page, limit }));
  }, [dispatch, page]);

  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

  const validateImage = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Only JPG, PNG or WEBP images are allowed";
    }
    return null;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const err = validateImage(file);
      if (err) { toast.error(err); return; }
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const err = validateImage(file);
      if (err) { toast.error(err); return; }
      setEditImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setEditImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const resetAddForm = () => {
    setName("");
    setDescription("");
    setButtonText("");
    setImage(null);
    setImagePreview(null);
    setErrorMsg("");
  };

  // ─── Create ────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setErrorMsg("Banner title is required");
      return;
    }
    if (trimmedName.length < 2) {
      setErrorMsg("Banner title must be at least 2 characters");
      return;
    }
    if (!image) {
      setErrorMsg("Banner image is required");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", trimmedName);
      formData.append("description", description);
      formData.append("buttonText", buttonText);
      formData.append("status", true);
      formData.append("image", image);

      await dispatch(createBanner(formData)).unwrap();

      toast.success("Banner added successfully");
      setShowModal(false);
      resetAddForm();
      dispatch(fetchBanners({ page, limit }));
    } catch {
      toast.error("Failed to add banner");
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await dispatch(deleteBanner(deleteId)).unwrap();
      toast.success("Banner deleted successfully");
      setShowDeleteModal(false);
      setDeleteId(null);
      setDeleteError("");
      dispatch(fetchBanners({ page, limit }));
    } catch (err) {
      setDeleteError(err);
    }
  };

  // ─── Update ────────────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!editName.trim()) return;

    try {
      const formData = new FormData();
      formData.append("name", editName);
      formData.append("description", editDescription);
      formData.append("buttonText", editButtonText);
      if (editImage) {
        formData.append("image", editImage);
      }

      await dispatch(updateBanner({ id: editId, formData })).unwrap();

      toast.success("Banner updated successfully");
      setShowEditModal(false);
      setEditId(null);
      setEditName("");
      setEditDescription("");
      setEditButtonText("");
      setEditImage(null);
      setEditImagePreview(null);
      setCurrentBannerImage(null);
      dispatch(fetchBanners({ page, limit }));
    } catch {
      toast.error("Failed to update banner");
    }
  };

  return (
    <div className="row">
      <div className="card mt-3">

        {/* Header */}
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title">Banner Management</h4>
          <button className="btn cusBTN" onClick={() => setShowModal(true)}>
            + Add Banner
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>SI</th>
                <th>Title</th>
                <th>Description</th>
                <th>Button Text</th>
                <th>Image</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">Loading...</td>
                </tr>
              ) : banners.length > 0 ? (
                banners.map((banner, index) => (
                  <tr key={banner._id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{banner.name}</td>
                    <td>{banner.description}</td>
                    <td>{banner.buttonText || "-"}</td>
                    <td>
                      {banner.image ? (
                        <img
                          src={`${process.env.REACT_APP_API_URL}/uploads/${banner.image}`}
                          alt=""
                          width={40}
                          height={40}
                          style={{
                            objectFit: "cover",
                            borderRadius: "6px",
                            backgroundColor: "#f1f1f1",
                          }}
                        />
                      ) : "-"}
                    </td>

                    <td>
                      <div
                        className={`status-toggle ${banner.status ? "enabled" : "disabled"}`}
                        onClick={() => dispatch(toggleBannerStatus(banner._id))}
                      >
                        <span className="toggle-knob"></span>
                        <span className="toggle-text">
                          {banner.status ? "Enabled" : "Disabled"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={() => {
                            setEditId(banner._id);
                            setEditName(banner.name);
                            setEditDescription(banner.description || "");
                            setEditButtonText(banner.buttonText || "");
                            setCurrentBannerImage(banner.image);
                            setEditImagePreview(null);
                            setShowEditModal(true);
                          }}
                        >
                          <iconify-icon icon="solar:pen-2-broken" />
                        </button>

                        <button
                          className="btn btn-soft-danger btn-sm"
                          onClick={() => {
                            setDeleteId(banner._id);
                            setShowDeleteModal(true);
                          }}
                        >
                          <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    No banners found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="card-footer border-top">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>

        {/* ── ADD BANNER MODAL ── */}
        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); resetAddForm(); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Add Banner</h5>
                <button className="btn-close" onClick={() => { setShowModal(false); resetAddForm(); }} />
              </div>

              <div className="modal-body">
                <label className="form-label">Title <span className="text-danger">*</span></label>
                <input
                  className={`form-control ${errorMsg ? "is-invalid" : ""}`}
                  placeholder="e.g. Premium Quality"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setErrorMsg(""); }}
                />
                {errorMsg && <div className="invalid-feedback d-block">{errorMsg}</div>}

                <label className="form-label mt-3">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="e.g. Every piece tells a story..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />

                <label className="form-label mt-3">Button Text</label>
                <input
                  className="form-control"
                  placeholder="e.g. View Collections"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value)}
                />

                <label className="form-label mt-3">Upload Image <span className="text-danger">*</span></label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                />

                {imagePreview && (
                  <div className="mt-3 text-center">
                    <p className="mb-2 fw-semibold">Preview:</p>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ width: "150px", height: "150px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn cusClose" onClick={() => { setShowModal(false); resetAddForm(); }}>
                  Cancel
                </button>
                <button className="btn cusBTNPop" onClick={handleCreate} disabled={submitting}>
                  {submitting ? "Adding..." : "Add Banner"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── DELETE BANNER MODAL ── */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setDeleteError(""); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Delete Banner</h5>
                <button className="btn-close" onClick={() => { setShowDeleteModal(false); setDeleteError(""); }} />
              </div>

              <div className="modal-body">
                <p className="mb-0 text-center">Are you sure you want to delete this banner?</p>
              </div>

              {deleteError && <div className="error text-center">{deleteError}</div>}

              <div className="modal-footer">
                <button className="btn cusClose" onClick={() => { setShowDeleteModal(false); setDeleteError(""); }}>
                  Cancel
                </button>
                <button className="btn cuscancel" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── EDIT BANNER MODAL ── */}
        {showEditModal && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5>Edit Banner</h5>
                <button className="btn-close" onClick={() => setShowEditModal(false)} />
              </div>

              <div className="modal-body">
                <label className="form-label">Title <span className="text-danger">*</span></label>
                <input
                  className="form-control"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />

                <label className="form-label mt-3">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />

                <label className="form-label mt-3">Button Text</label>
                <input
                  className="form-control"
                  value={editButtonText}
                  onChange={(e) => setEditButtonText(e.target.value)}
                />

                <label className="form-label mt-3">Upload Image</label>
                <input
                  type="file"
                  className="form-control mt-1"
                  accept="image/*"
                  onChange={handleEditImageChange}
                />

                {(editImagePreview || currentBannerImage) && (
                  <div className="mt-3 text-center">
                    <p className="mb-2 fw-semibold">
                      {editImagePreview ? "New Image Preview:" : "Current Image:"}
                    </p>
                    <img
                      src={editImagePreview || `${process.env.REACT_APP_API_URL}/uploads/${currentBannerImage}`}
                      alt="Banner"
                      style={{ width: "150px", height: "150px", objectFit: "cover", borderRadius: "8px", border: "1px solid #ddd" }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn cusClose" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button className="btn cusBTNPop" onClick={handleUpdate}>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}